import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteDriveLinkMutation, getReleaseQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteDriveLink = (entityId, entityType) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteDriveLinkAction, { loading }] = useMutation(deleteDriveLinkMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getReleaseQuery,
                variables: {
                    where: { id: entityId },
                },
            },
        ],
    });

    const { deleteDriveLinkPending: deleteDriveLinkPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteDriveLink = async (id) => {
        try {
            await deleteDriveLinkAction({
                onCompleted: () => {
                    toast.dismiss(`${deleteDriveLinkPendingToastName}_${toastId}`);

                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: `${entityType} ${t("labels.driveLink")}` }),
                            statusTypesData.success,
                        ),
                    );
                },
                onError: () => toast.dismiss(`${deleteDriveLinkPendingToastName}_${toastId}`),
                variables: { id },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(
                    () => t("messages.deleteMsgs.pending", { entity: `${entityType} ${t("labels.driveLink")}` }),
                    { toastId: `${deleteDriveLinkPendingToastName}_${toastId}` },
                );
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteDriveLink };
};

export default useDeleteDriveLink;
